import { TrendingUp, DollarSign, BarChart3, HelpCircle } from 'lucide-react';
import { CHAIN_LIMITS } from '../../utils/bundleCalc';
import {
  formatNumber, formatCurrency, formatTokenPrice,
  formatTokenPriceNative, formatCurrencyTiny,
} from '../../utils/bundleFormatters';
import { BundleTooltip } from './BundleTooltip';

export default function ResultsSection({ results, chain, initialLiquidity, baseCurrencyPrice, baseSymbol }) {
  const chainMeta = CHAIN_LIMITS[chain] || CHAIN_LIMITS.ETH;
  const sym = baseSymbol || chainMeta.nativeSymbol;
  const initialLpUsd = Number(initialLiquidity) * Number(baseCurrencyPrice);
  const totalRequiredUsd = initialLpUsd + results.bundleCostUsd;
  const totalRequiredBase = Number(initialLiquidity) + results.bundleCost;

  return (
    <div className="rounded-xl border border-border bg-card p-4 sm:p-6 shadow-sm">
      <h2 className="text-base sm:text-lg font-semibold mb-4 flex items-center" data-testid="bundle-results-title">
        <BarChart3 className="mr-2 w-4 h-4 text-primary" />
        Bundle Results
      </h2>

      {/* Headline card */}
      <div className="mb-5 p-4 rounded-lg bg-gradient-to-br from-primary/5 to-emerald-500/5 border border-primary/30">
        <h3 className="text-sm font-semibold mb-3 flex items-center">
          <DollarSign className="mr-1.5 w-4 h-4 text-emerald-500" />
          Total Capital Required
        </h3>
        <div className="space-y-2.5">
          <Row label="Initial LP Deposit" tooltip={`${sym} added at launch`} usd={initialLpUsd} native={initialLiquidity} sym={sym} />
          <Row label="Bundle Buy Cost"    tooltip={`${sym} spent across bundling wallets`} usd={results.bundleCostUsd} native={results.bundleCost} sym={sym} />
          <div className="pt-2 border-t border-border flex items-center justify-between">
            <span className="text-sm font-semibold">Total Required</span>
            <div className="text-right">
              <div className="text-lg font-bold text-emerald-500 font-mono" data-testid="bundle-total-required-usd">{formatCurrency(totalRequiredUsd)}</div>
              <div className="text-xs text-muted-foreground font-mono">{formatTokenPrice(totalRequiredBase)} {sym}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Metric grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MetricCard
          label="Total Base in Pool (Final)"
          tooltip="Base currency in pool after bundling"
          primary={formatCurrency(results.totalBaseInPoolUsd)}
          secondary={`${formatTokenPrice(results.totalBaseInPool)} ${sym}`}
          testid="bundle-metric-pool-base"
        />
        <MetricCard
          label="Token Price (Final)"
          tooltip="Token price after bundling buys"
          primary={formatCurrencyTiny(results.finalTokenPriceUsd)}
          secondary={`${formatTokenPriceNative(results.finalTokenPrice)} ${sym}`}
          accent={`${formatNumber(results.priceIncreaseMultiple, 2)}× initial`}
          testid="bundle-metric-final-price"
        />
        <MetricCard
          label="Token Price (Initial)"
          tooltip="Price immediately after adding liquidity"
          primary={formatCurrencyTiny(results.initialTokenPriceUsd)}
          secondary={`${formatTokenPriceNative(results.initialTokenPrice)} ${sym}`}
          testid="bundle-metric-initial-price"
        />
        <MetricCard
          label="Fully Diluted Value"
          tooltip="Market cap if all tokens sold at final price"
          primary={formatCurrency(results.fullyDilutedValueUsd)}
          secondary={`${formatTokenPrice(results.fullyDilutedValueBase)} ${sym}`}
          testid="bundle-metric-fdv"
        />
        <MetricCard
          label="Liquidity Pool Value"
          tooltip="Total USD in the LP after bundling (both sides)"
          primary={formatCurrency(results.liquidityPoolValueUsd)}
          secondary={`FDV/LP ratio: ${formatNumber(results.fdvToLpRatio, 2)}×`}
          full
          testid="bundle-metric-lp-value"
        />
      </div>
    </div>
  );
}

function Row({ label, tooltip, usd, native, sym }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center text-sm text-muted-foreground">
        {label}
        {tooltip && (
          <BundleTooltip content={tooltip}>
            <HelpCircle className="ml-1 w-3 h-3 text-muted-foreground/60" />
          </BundleTooltip>
        )}
      </div>
      <div className="text-right">
        <div className="text-sm font-semibold font-mono">{formatCurrency(usd)}</div>
        <div className="text-xs text-muted-foreground font-mono">{formatTokenPrice(native)} {sym}</div>
      </div>
    </div>
  );
}

function MetricCard({ label, tooltip, primary, secondary, accent, full, testid }) {
  return (
    <div className={`p-3.5 rounded-lg bg-muted/40 border border-border ${full ? 'md:col-span-2' : ''}`} data-testid={testid}>
      <div className="flex items-center text-xs text-muted-foreground mb-1.5">
        {label}
        {tooltip && (
          <BundleTooltip content={tooltip}>
            <HelpCircle className="ml-1 w-3 h-3 text-muted-foreground/60" />
          </BundleTooltip>
        )}
      </div>
      <div className="text-base font-bold font-mono">{primary}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5 font-mono">{secondary}</div>
      {accent && (
        <div className="text-[11px] text-emerald-500 mt-1 flex items-center">
          <TrendingUp className="w-3 h-3 mr-1" /> {accent}
        </div>
      )}
    </div>
  );
}
